import React from "react";
import CardLayout from "../../../../../components/layouts/CardLayout";
import MiniFadedChart from "../../../../../components/icons/MiniFadedChartIcon";
import ReactMarkdown from "react-markdown";
import { Text } from "../../../../../modules/Dashboard";

// ======================
// Section color theme
// ======================
const sectionStyles = {
    AnalyticalApproach: {
        titleColor: "text-blue-400",
        cardClass: "border-blue-500",
    },
    StructuralEnhancements: {
        titleColor: "text-indigo-400",
        cardClass: "border-indigo-500",
    },
    KeyLevelsAndZones: {
        titleColor: "text-yellow-400",
        cardClass: "border-yellow-500",
    },
    Scenarios: {
        titleColor: "text-teal-400",
        cardClass: "border-teal-500",
    },
    ViewChange: {
        titleColor: "text-purple-400",
        cardClass: "border-purple-500",
    },
    ConfidenceScore: {
        titleColor: "text-orange-400",
        cardClass: "border-orange-500",
    },
    RiskAndLeverage: {
        titleColor: "text-rose-400",
        cardClass: "border-rose-500",
    },
    Summary: {
        titleColor: "text-cyan-400",
        cardClass: "border-cyan-500",
    },
};

// ======================
// Recursive Renderer
// ======================
const renderObject = (obj) =>
    Object.entries(obj).map(([key, value], idx) => {
        if (!value) return null;

        if (typeof value === "string") {
            return (
                <div
                    key={idx}
                    className="rounded-xl p-3 bg-[#1E222A] flex flex-col gap-2"
                >
                    <Text
                        tid={key}
                        className="!text-mainColor font-semibold text-sm"
                    />
                    <Text tid={value} className="text-sm text-white/80" />
                </div>
            );
        }

        if (typeof value === "object") {
            return (
                <div key={idx} className="flex flex-col gap-2">
                    <Text
                        tid={key}
                        className="!text-mainColor font-semibold text-base"
                    />
                    <div className="grid grid-cols-1 gap-3 pl-3 border-l border-white/10">
                        {renderObject(value)}
                    </div>
                </div>
            );
        }

        return null;
    });

// ======================
// Scenario Card
// ======================
const ScenarioCard = ({ scenarioKey, scenario }) => {
    const metaFields = [
        "Direction",
        "Entry",
        "StopLoss",
        "ActivationCondition",
    ];

    const explanationFields = [
        "ScenarioLogic",
        "StructuralReasoning",
        "RiskNote",
        "Target3Note",
    ];

    return (
        <CardLayout className="border-teal-500 flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <MiniFadedChart />
                <Text
                    tid={scenarioKey}
                    className="text-xl font-semibold text-teal-400"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {metaFields.map(
                    (field) =>
                        scenario[field] && (
                            <div
                                key={field}
                                className="bg-[#1E222A] rounded-xl p-3"
                            >
                                <Text
                                    tid={field}
                                    className="!text-mainColor font-semibold text-sm"
                                />
                                <Text
                                    tid={scenario[field]}
                                    className="text-sm"
                                />
                            </div>
                        )
                )}
            </div>

            {scenario.Targets && (
                <div className="bg-[#1E222A] rounded-xl p-3">
                    <Text
                        tid="Targets"
                        className="!text-mainColor font-semibold text-sm mb-2"
                    />
                    <div className="flex flex-wrap gap-3">
                        {Object.entries(scenario.Targets).map(([t, v]) => (
                            <Text
                                key={t}
                                tid={`${t}: ${v}`}
                                className="text-sm"
                            />
                        ))}
                    </div>
                </div>
            )}

            {explanationFields.map(
                (field) =>
                    scenario[field] && (
                        <Text
                            key={field}
                            tid={scenario[field]}
                            className="text-sm text-white/80 leading-relaxed"
                        />
                    )
            )}
        </CardLayout>
    );
};

// ======================
// Main Component
// ======================
const BreakoutStructure = ({ advance }) => {
    if (!advance || typeof advance !== "object") return null;

    return (
        <div className="grid grid-cols-1 gap-6">
            {Object.entries(advance).map(([key, content], idx) => {
                const styles = sectionStyles[key] || sectionStyles.Summary;

                // Scenarios handled separately
                if (key === "Scenarios" && typeof content === "object") {
                    return (
                        <div key={idx} className="grid grid-cols-1 gap-6">
                            {Object.entries(content).map(
                                ([scenarioKey, scenario]) => (
                                    <ScenarioCard
                                        key={scenarioKey}
                                        scenarioKey={scenarioKey}
                                        scenario={scenario}
                                    />
                                )
                            )}
                        </div>
                    );
                }

                // String sections
                if (typeof content === "string") {
                    return (
                        <CardLayout
                            key={idx}
                            className={`flex flex-col gap-3 ${styles.cardClass}`}
                        >
                            <div className="flex items-center gap-2">
                                <MiniFadedChart />
                                <ReactMarkdown
                                    children={key}
                                    className={`text-xl font-semibold ${styles.titleColor}`}
                                />
                            </div>
                            <ReactMarkdown className="text-white/80 leading-relaxed">
                                {content}
                            </ReactMarkdown>
                        </CardLayout>
                    );
                }

                // Object sections
                if (typeof content === "object") {
                    return (
                        <CardLayout
                            key={idx}
                            className={`flex flex-col gap-4 ${styles.cardClass}`}
                        >
                            <div className="flex items-center gap-2">
                                <MiniFadedChart />
                                <ReactMarkdown
                                    children={key}
                                    className={`text-xl font-semibold ${styles.titleColor}`}
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-4">
                                {renderObject(content)}
                            </div>
                        </CardLayout>
                    );
                }

                return null;
            })}
        </div>
    );
};

export default BreakoutStructure;
